/**
 * Legacy path → V2 route (frontend phase-14 §2.2).
 *
 * A landlord's bookmarks and every link in every email APMP has ever sent point at legacy
 * paths. This is where each one lands in V2.
 *
 * **Generated from `rollout/bridge.py::REDIRECT_MAP`** — the backend owns the table, because
 * the backend is what serves the legacy portal's own banner and needs the same map. Regenerate
 * with `node scripts/gen-legacy-redirects.mjs`; `__tests__/legacy-redirects.spec.ts` asserts
 * this file still agrees with `GET /api/v2/public/rollout/legacy-redirects/`.
 *
 * Only routes with a real V2 equivalent are here. Sending somebody from a page that works to a
 * page that does not exist is worse than leaving the link alone, so an unmapped legacy path is
 * left exactly as it is — the reasons are in `UNMAPPED` below, and "it is not in the map" is an
 * answer somebody wrote down rather than an omission.
 */

/** Exact paths. */
export const LEGACY_REDIRECTS: Readonly<Record<string, string>> = Object.freeze({
  '/change_password/': '/app/settings/password',
  '/login/': '/auth/login',
  '/portal-v2/': '/app/landlord',
  '/portal-v2/analytics/occupants/': '/app/landlord/analytics?report=occupancy',
  '/portal-v2/analytics/overview/': '/app/landlord/analytics',
  '/portal-v2/analytics/properties/': '/app/landlord/analytics?report=portfolio',
  '/portal-v2/analytics/revenue/': '/app/landlord/analytics?report=revenue',
  '/portal-v2/auth/login/': '/auth/login',
  '/portal-v2/auth/password-change/': '/app/settings/password',
  '/portal-v2/estates/': '/app/landlord/estates',
  '/portal-v2/estates/create/': '/app/landlord/estates?create=1',
  '/portal-v2/occupants/': '/app/landlord/tenants',
  '/portal-v2/payments/': '/app/landlord/payments',
  '/portal-v2/profile/': '/app/landlord/settings/profile',
  '/portal-v2/profile/edit/': '/app/landlord/settings/profile',
  '/portal-v2/properties/': '/app/landlord/properties',
  '/portal-v2/properties/create/': '/app/landlord/properties/new',
  '/profile/': '/app/landlord/settings/profile',
  '/property-management-portal/dashboard/': '/app/landlord',
  '/property-management-portal/estates/': '/app/landlord/estates',
  '/property-management-portal/login/': '/auth/login',
  '/property-management-portal/occupants/': '/app/landlord/tenants',
  '/property-management-portal/properties/': '/app/landlord/properties',
  '/property-management-portal/properties/add/': '/app/landlord/properties/new',
  '/property/my_properties/': '/app/landlord/properties',
  '/property/properties/': '/listings',
})

/**
 * Parameterised paths. Keys use vue-router's `:name` syntax; the destination reuses the same
 * names, so `/portal-v2/properties/41/` becomes `/app/landlord/properties/41`.
 */
export const LEGACY_REDIRECT_PATTERNS: Readonly<Record<string, string>> = Object.freeze({
  '/portal-v2/occupants/:occupant_id/': '/app/landlord/tenants/:occupant_id',
  '/portal-v2/properties/:property_id/': '/app/landlord/properties/:property_id',
  '/portal-v2/properties/:property_id/edit/': '/app/landlord/properties/:property_id/edit',
  '/portal-v2/spaces/property/:property_id/': '/app/landlord/properties/:property_id?tab=spaces',
})

/** Legacy paths that deliberately have no V2 destination, and why. */
export const UNMAPPED: Readonly<Record<string, string>> = Object.freeze({
  '/dashboard/': 'The internal staff dashboard is a separate product from the V2 console.',
  '/dashboard/zones/': 'Zones are staff tooling with no V2 equivalent.',
  '/propertydashio/tenant_homepage/': "The tenant domain is permanently out of V2's scope.",
  '/propertydashio/tenant_login_page/': 'Tenant authentication stays on V1 (scope-freeze.md §2).',
  '/rent/payment/initiate/:space_id/':
    "V1's own rent flow. Untouched, and still the only way an existing tenant pays.",
  '/worker/worker_homepage/': 'The worker portal has no V2 equivalent and is not in scope.',
})

/**
 * The V2 route for a legacy path, or `null`.
 *
 * Exact matches first, then the patterns, so `/portal-v2/properties/create/` cannot be
 * swallowed by `/portal-v2/properties/:property_id/`.
 */
export function resolveLegacyRedirect(path: string): string | null {
  const normalised = path.endsWith('/') ? path : `${path}/`
  const exact = LEGACY_REDIRECTS[normalised]
  if (exact) return exact

  for (const [pattern, destination] of Object.entries(LEGACY_REDIRECT_PATTERNS)) {
    const names: string[] = []
    const source = pattern.replace(/:([A-Za-z_][A-Za-z0-9_]*)/g, (_match, name: string) => {
      names.push(name)
      return '([^/]+)'
    })
    const found = new RegExp(`^${source}$`).exec(normalised)
    if (!found) continue
    return names.reduce(
      (result, name, index) => result.replace(`:${name}`, found[index + 1] ?? ''),
      destination,
    )
  }
  return null
}
